// Charts on a sheet board itself (Fixes 9, H2b), as Google Sheets floats one over its cells: a block of cells drawn as
// a column, line or pie chart (any of the slides' types), placed beside the cells and read again from them whenever a
// value changes, so it never shows a number the sheet does not hold. The chart is drawn by the slides' own chart code
// (src/boards/slides/chart/ChartView.tsx) from the block as the sheet shows it (blockData in shared/slideChart.ts, the
// same reading a slide chart linked to a sheet uses).
//
// Kept on the tab (SheetTabData.charts): its id, its block ("A1:B13"), its type, a title, series in rows, the cell its
// top left corner sits on, and its size in pixels. It moves with its cells when rows or columns are put in or taken out.

import { MAX_COLS, MAX_ROWS, addrOf, parseAddr, parseRect, rectName } from "./addr";
import { shiftFormula } from "./shift";
import { randomString } from "../ids";

export const SHEET_CHART_TYPES = ["column", "bar", "line", "area", "pie", "doughnut", "scatter"] as const;
export type SheetChartType = (typeof SHEET_CHART_TYPES)[number];
export const MAX_SHEET_CHARTS = 20;

export interface SheetChart {
  id: string;
  /** the block it draws: "A1:B13" */
  range: string;
  type: SheetChartType;
  title?: string;
  /** series in rows rather than columns */
  rows?: boolean;
  /** the cell its top left corner sits on, and its size in pixels */
  at: string;
  w: number;
  h: number;
}

const ID = /^[A-Za-z0-9_-]{4,40}$/;

/** A tab's charts, each sound; undefined for none. */
export function cleanCharts(raw: unknown): SheetChart[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: SheetChart[] = [];
  for (const v of raw) {
    if (out.length >= MAX_SHEET_CHARTS || !v || typeof v !== "object") continue;
    const r = v as Record<string, unknown>;
    const range = typeof r.range === "string" ? r.range.trim().replace(/\$/g, "").toUpperCase() : "";
    const x = /^[A-Z]{1,3}\d{1,5}(:[A-Z]{1,3}\d{1,5})?$/.test(range) ? parseRect(range) : null;
    const at = typeof r.at === "string" ? parseAddr(r.at) : null;
    if (typeof r.id !== "string" || !ID.test(r.id) || !x || !at || out.some((c) => c.id === r.id)) continue;
    const type = (SHEET_CHART_TYPES as readonly unknown[]).includes(r.type) ? (r.type as SheetChartType) : "column";
    const size = (n: unknown, d: number, lo: number, hi: number) => (typeof n === "number" && Number.isFinite(n) ? Math.round(Math.max(lo, Math.min(hi, n))) : d);
    const title = typeof r.title === "string" ? r.title.replace(/[\u0000-\u001f]+/g, " ").trim().slice(0, 120) : "";
    out.push({ id: r.id, range: rectName(x), type, ...(title ? { title } : {}), ...(r.rows === true ? { rows: true } : {}), at: addrOf(at.r, at.c), w: size(r.w, 480, 200, 1400), h: size(r.h, 300, 140, 900) });
  }
  return out.length ? out : undefined;
}

/** A tab's charts once rows or columns were put in or taken out there: their blocks and places move with the cells. */
export function shiftCharts(list: SheetChart[], tabName: string, l: { axis: "row" | "col"; at: number; n: number; del: boolean }): SheetChart[] | null {
  const ch = { tab: tabName, ...l };
  const out: SheetChart[] = [];
  for (const c of list) {
    const range = shiftFormula(`=${c.range}`, tabName, ch).slice(1);
    if (!parseRect(range)) continue;
    // its place: a cell taken out leaves it on the one that took its place
    const p = parseAddr(c.at)!;
    const moved = shiftFormula(`=${c.at}`, tabName, ch).slice(1);
    const at = parseAddr(moved) ? moved : addrOf(Math.min(p.r, MAX_ROWS - 1), Math.min(p.c, MAX_COLS - 1));
    out.push({ ...c, range, at });
  }
  return out.length ? out : null;
}

/** A new chart's id. */
export function chartId(): string {
  return `ch${randomString(8)}`;
}

export const CHART_WORDS: Record<SheetChartType, string> = { column: "Column", bar: "Bar", line: "Line", area: "Area", pie: "Pie", doughnut: "Doughnut", scatter: "Scatter" };
